import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Label } from '../../ui/label';
import { Input } from '../../ui/input';
import { Button } from '../../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Badge } from '../../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Alert, AlertDescription, AlertTitle } from '../../ui/alert';
import { GitBranch, Target, AlertCircle } from 'lucide-react';
import { ParsedWorksheet } from './worksheet';

interface DOEOptimizerProps {
  worksheet: ParsedWorksheet;
}

interface FactorSetting {
  name: string;
  low: number;
  high: number;
}

const formatNumber = (value: number) => (Number.isFinite(value) ? value.toFixed(4) : '—');

const transpose = (matrix: number[][]) => matrix[0].map((_, index) => matrix.map((row) => row[index]));

const matrixMultiply = (a: number[][], b: number[][]) => {
  return a.map((row) =>
    b[0].map((_, columnIndex) => row.reduce((sum, value, rowIndex) => sum + value * b[rowIndex][columnIndex], 0)),
  );
};

const matrixVectorMultiply = (matrix: number[][], vector: number[]) => {
  return matrix.map((row) => row.reduce((sum, value, index) => sum + value * vector[index], 0));
};

const invertMatrix = (matrix: number[][]) => {
  const size = matrix.length;
  const augmented = matrix.map((row, rowIndex) => [
    ...row,
    ...Array.from({ length: size }, (_, columnIndex) => (columnIndex === rowIndex ? 1 : 0)),
  ]);

  for (let pivotIndex = 0; pivotIndex < size; pivotIndex += 1) {
    let maxRow = pivotIndex;
    for (let rowIndex = pivotIndex + 1; rowIndex < size; rowIndex += 1) {
      if (Math.abs(augmented[rowIndex][pivotIndex]) > Math.abs(augmented[maxRow][pivotIndex])) {
        maxRow = rowIndex;
      }
    }

    [augmented[pivotIndex], augmented[maxRow]] = [augmented[maxRow], augmented[pivotIndex]];
    const pivot = augmented[pivotIndex][pivotIndex];
    if (Math.abs(pivot) < 1e-10) {
      throw new Error('Singular design matrix');
    }

    for (let columnIndex = 0; columnIndex < size * 2; columnIndex += 1) {
      augmented[pivotIndex][columnIndex] /= pivot;
    }

    for (let rowIndex = 0; rowIndex < size; rowIndex += 1) {
      if (rowIndex === pivotIndex) continue;
      const factor = augmented[rowIndex][pivotIndex];
      for (let columnIndex = 0; columnIndex < size * 2; columnIndex += 1) {
        augmented[rowIndex][columnIndex] -= factor * augmented[pivotIndex][columnIndex];
      }
    }
  }

  return augmented.map((row) => row.slice(size));
};

const buildTermNames = (factorNames: string[]) => {
  const terms = ['Intercept', ...factorNames];
  if (factorNames.length >= 2) {
    terms.push(`${factorNames[0]}×${factorNames[1]}`);
  }
  if (factorNames.length >= 3) {
    terms.push(`${factorNames[0]}×${factorNames[2]}`);
    terms.push(`${factorNames[1]}×${factorNames[2]}`);
    terms.push(`${factorNames[0]}×${factorNames[1]}×${factorNames[2]}`);
  }
  return terms;
};

const buildModelRow = (codedLevels: number[]) => {
  if (codedLevels.length === 2) {
    const [a, b] = codedLevels;
    return [1, a, b, a * b];
  }

  const [a, b, c] = codedLevels;
  return [1, a, b, c, a * b, a * c, b * c, a * b * c];
};

export const DOEOptimizer: React.FC<DOEOptimizerProps> = ({ worksheet }) => {
  const [factorCount, setFactorCount] = useState<'2' | '3'>('2');
  const [goal, setGoal] = useState<'maximize' | 'minimize' | 'target'>('maximize');
  const [targetValue, setTargetValue] = useState('100');
  const [factorAName, setFactorAName] = useState('Temperature');
  const [factorALow, setFactorALow] = useState('150');
  const [factorAHigh, setFactorAHigh] = useState('180');
  const [factorBName, setFactorBName] = useState('Pressure');
  const [factorBLow, setFactorBLow] = useState('40');
  const [factorBHigh, setFactorBHigh] = useState('60');
  const [factorCName, setFactorCName] = useState('Time');
  const [factorCLow, setFactorCLow] = useState('8');
  const [factorCHigh, setFactorCHigh] = useState('12');
  const [responseIntercept, setResponseIntercept] = useState('100');
  const [responseA, setResponseA] = useState('6');
  const [responseB, setResponseB] = useState('4');
  const [responseC, setResponseC] = useState('3');
  const [responseAB, setResponseAB] = useState('1.5');
  const [responseAC, setResponseAC] = useState('0.5');
  const [responseBC, setResponseBC] = useState('0.75');
  const [responseABC, setResponseABC] = useState('0.25');
  const [factorColumnIds, setFactorColumnIds] = useState<string[]>(worksheet.numericColumns.slice(0, 3).map((column) => column.id));
  const [responseColumnId, setResponseColumnId] = useState(worksheet.numericColumns[3]?.id ?? worksheet.numericColumns[0]?.id ?? '');

  const factorSettings = useMemo<FactorSetting[]>(() => {
    const base = [
      { name: factorAName || 'Factor A', low: Number(factorALow), high: Number(factorAHigh) },
      { name: factorBName || 'Factor B', low: Number(factorBLow), high: Number(factorBHigh) },
      { name: factorCName || 'Factor C', low: Number(factorCLow), high: Number(factorCHigh) },
    ];
    return base.slice(0, Number(factorCount));
  }, [factorAHigh, factorALow, factorAName, factorBHigh, factorBLow, factorBName, factorCHigh, factorCLow, factorCName, factorCount]);

  const designRuns = useMemo(() => {
    const runs = Array.from({ length: 2 ** factorSettings.length }, (_, index) => {
      const codedLevels = factorSettings.map((_, factorIndex) => ((index >> factorIndex) & 1 ? 1 : -1));
      const actualLevels = factorSettings.map((factor, factorIndex) => (codedLevels[factorIndex] === -1 ? factor.low : factor.high));
      return { run: index + 1, codedLevels, actualLevels };
    });
    return runs;
  }, [factorSettings]);

  const manualModel = useMemo(() => {
    const coefficients = [
      Number(responseIntercept),
      Number(responseA),
      Number(responseB),
      ...(Number(factorCount) === 3 ? [Number(responseC)] : []),
      Number(responseAB),
      ...(Number(factorCount) === 3 ? [Number(responseAC), Number(responseBC), Number(responseABC)] : []),
    ];

    if (coefficients.some((value) => !Number.isFinite(value))) {
      return null;
    }

    const predictions = designRuns.map((run) => {
      const modelRow = buildModelRow(run.codedLevels);
      const predicted = modelRow.reduce((sum, value, index) => sum + value * coefficients[index], 0);
      return { ...run, predicted };
    });

    return { coefficients, predictions };
  }, [designRuns, factorCount, responseA, responseAB, responseABC, responseAC, responseB, responseBC, responseC, responseIntercept]);

  const fittedModel = useMemo(() => {
    const neededFactors = Number(factorCount);
    if (factorColumnIds.length < neededFactors || !responseColumnId) return null;

    const factorColumns = factorColumnIds
      .slice(0, neededFactors)
      .map((columnId) => worksheet.numericColumns.find((column) => column.id === columnId))
      .filter((column): column is NonNullable<typeof column> => Boolean(column));
    const responseColumn = worksheet.numericColumns.find((column) => column.id === responseColumnId);

    if (factorColumns.length !== neededFactors || !responseColumn) return null;

    const maxRows = Math.min(...factorColumns.map((column) => column.numericValues.length), responseColumn.numericValues.length);
    if (maxRows < 4) return null;

    const factorMins = factorColumns.map((column) => Math.min(...column.numericValues.slice(0, maxRows)));
    const factorMaxes = factorColumns.map((column) => Math.max(...column.numericValues.slice(0, maxRows)));

    if (factorMins.some((min, index) => factorMaxes[index] === min)) return null;

    const xRows = Array.from({ length: maxRows }, (_, rowIndex) => {
      const codedLevels = factorColumns.map((column, factorIndex) => {
        const min = factorMins[factorIndex];
        const max = factorMaxes[factorIndex];
        const midpoint = (max + min) / 2;
        const halfRange = (max - min) / 2;
        return (column.numericValues[rowIndex] - midpoint) / halfRange;
      });
      return buildModelRow(codedLevels);
    });

    const y = responseColumn.numericValues.slice(0, maxRows);

    try {
      const xt = transpose(xRows);
      const xtx = matrixMultiply(xt, xRows);
      const inverse = invertMatrix(xtx);
      const coefficients = matrixVectorMultiply(inverse, matrixVectorMultiply(xt, y));
      const predictions = designRuns.map((run) => ({
        ...run,
        predicted: buildModelRow(run.codedLevels).reduce((sum, value, index) => sum + value * coefficients[index], 0),
      }));
      return {
        coefficients,
        predictions,
        factorNames: factorColumns.map((column) => column.name),
        responseName: responseColumn.name,
      };
    } catch {
      return null;
    }
  }, [designRuns, factorColumnIds, factorCount, responseColumnId, worksheet.numericColumns]);

  const optimize = (predictions: Array<{ predicted: number; actualLevels: number[]; run: number }>) => {
    if (goal === 'maximize') {
      return predictions.reduce((best, run) => (run.predicted > best.predicted ? run : best), predictions[0]);
    }
    if (goal === 'minimize') {
      return predictions.reduce((best, run) => (run.predicted < best.predicted ? run : best), predictions[0]);
    }
    const target = Number(targetValue);
    return predictions.reduce((best, run) =>
      Math.abs(run.predicted - target) < Math.abs(best.predicted - target) ? run : best,
    predictions[0]);
  };

  const manualOptimum = manualModel ? optimize(manualModel.predictions) : null;
  const fittedOptimum = fittedModel ? optimize(fittedModel.predictions) : null;
  const activeModel = fittedModel ?? manualModel;
  const activeOptimum = fittedOptimum ?? manualOptimum;
  const termNames = buildTermNames(factorSettings.map((factor) => factor.name));

  const updateFactorColumn = (index: number, value: string) => {
    setFactorColumnIds((current) => {
      const next = [...current];
      next[index] = value;
      return next;
    });
  };

  if (worksheet.numericColumns.length === 0) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>No numeric worksheet columns</AlertTitle>
        <AlertDescription>Paste numeric worksheet data to fit a DOE response model, or use the manual coefficient inputs to plan a factorial design.</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3">
        <div className="rounded-xl bg-indigo-600 p-3 text-white">
          <GitBranch size={24} />
        </div>
        <div>
          <div className="mb-2 flex items-center gap-2">
            <Badge>Ready</Badge>
            <Badge variant="outline">DOE + Optimizer</Badge>
          </div>
          <h2 className="text-2xl font-semibold text-foreground">DOE + Response Optimizer</h2>
          <p className="text-muted-foreground">Generate two-level factorial designs, fit a coded response model, and identify the best operating corner for your goal.</p>
        </div>
      </div>

      <Tabs defaultValue="design" className="space-y-4">
        <TabsList>
          <TabsTrigger value="design">Design space</TabsTrigger>
          <TabsTrigger value="manual">Manual model</TabsTrigger>
          <TabsTrigger value="fit">Fit from worksheet</TabsTrigger>
        </TabsList>

        <TabsContent value="design" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Factor definitions</CardTitle>
              <CardDescription>Define 2 or 3 factors with low / high settings for a full factorial screening design.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label>Factor count</Label>
                  <Select value={factorCount} onValueChange={(value: '2' | '3') => setFactorCount(value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="2">2 factors</SelectItem>
                      <SelectItem value="3">3 factors</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Optimization goal</Label>
                  <Select value={goal} onValueChange={(value: 'maximize' | 'minimize' | 'target') => setGoal(value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="maximize">Maximize</SelectItem>
                      <SelectItem value="minimize">Minimize</SelectItem>
                      <SelectItem value="target">Hit target</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Target response</Label>
                  <Input value={targetValue} onChange={(event) => setTargetValue(event.target.value)} disabled={goal !== 'target'} />
                </div>
              </div>

              <div className="grid gap-4 lg:grid-cols-3">
                {factorSettings.map((factor, index) => (
                  <Card key={factor.name}>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base">Factor {index + 1}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="space-y-2">
                        <Label>Name</Label>
                        <Input
                          value={factor.name}
                          onChange={(event) => {
                            if (index === 0) setFactorAName(event.target.value);
                            if (index === 1) setFactorBName(event.target.value);
                            if (index === 2) setFactorCName(event.target.value);
                          }}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label>Low</Label>
                          <Input
                            value={String(factor.low)}
                            onChange={(event) => {
                              if (index === 0) setFactorALow(event.target.value);
                              if (index === 1) setFactorBLow(event.target.value);
                              if (index === 2) setFactorCLow(event.target.value);
                            }}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>High</Label>
                          <Input
                            value={String(factor.high)}
                            onChange={(event) => {
                              if (index === 0) setFactorAHigh(event.target.value);
                              if (index === 1) setFactorBHigh(event.target.value);
                              if (index === 2) setFactorCHigh(event.target.value);
                            }}
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Generated factorial design</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Run</TableHead>
                        {factorSettings.map((factor) => <TableHead key={factor.name}>{factor.name}</TableHead>)}
                        {factorSettings.map((factor) => <TableHead key={`${factor.name}-coded`}>{factor.name} (coded)</TableHead>)}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {designRuns.map((run) => (
                        <TableRow key={run.run}>
                          <TableCell>{run.run}</TableCell>
                          {run.actualLevels.map((value, index) => <TableCell key={`${run.run}-actual-${index}`}>{formatNumber(value)}</TableCell>)}
                          {run.codedLevels.map((value, index) => <TableCell key={`${run.run}-coded-${index}`}>{value}</TableCell>)}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="manual" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Manual coded model</CardTitle>
              <CardDescription>Enter coded-factor coefficients and evaluate the generated design corners immediately.</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              {[
                ['Intercept', responseIntercept, setResponseIntercept],
                [factorSettings[0]?.name ?? 'A', responseA, setResponseA],
                [factorSettings[1]?.name ?? 'B', responseB, setResponseB],
                ...(Number(factorCount) === 3 ? [[factorSettings[2]?.name ?? 'C', responseC, setResponseC]] : []),
                [`${factorSettings[0]?.name ?? 'A'}×${factorSettings[1]?.name ?? 'B'}`, responseAB, setResponseAB],
                ...(Number(factorCount) === 3
                  ? [
                      [`${factorSettings[0]?.name ?? 'A'}×${factorSettings[2]?.name ?? 'C'}`, responseAC, setResponseAC],
                      [`${factorSettings[1]?.name ?? 'B'}×${factorSettings[2]?.name ?? 'C'}`, responseBC, setResponseBC],
                      [`${factorSettings[0]?.name ?? 'A'}×${factorSettings[1]?.name ?? 'B'}×${factorSettings[2]?.name ?? 'C'}`, responseABC, setResponseABC],
                    ]
                  : []),
              ].map(([label, value, setter]) => (
                <div key={String(label)} className="space-y-2">
                  <Label>{String(label)}</Label>
                  <Input value={String(value)} onChange={(event) => (setter as (value: string) => void)(event.target.value)} />
                </div>
              ))}
            </CardContent>
          </Card>

          {manualModel && manualOptimum && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg"><Target size={18} /> Manual optimum</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-4">
                  <div className="rounded-lg border p-3">Best run: {manualOptimum.run}</div>
                  <div className="rounded-lg border p-3">Predicted response: {formatNumber(manualOptimum.predicted)}</div>
                  {factorSettings.map((factor, index) => (
                    <div key={factor.name} className="rounded-lg border p-3">{factor.name}: {formatNumber(manualOptimum.actualLevels[index])}</div>
                  ))}
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Term</TableHead>
                      <TableHead>Coefficient</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {termNames.map((term, index) => (
                      <TableRow key={term}>
                        <TableCell>{term}</TableCell>
                        <TableCell>{formatNumber(manualModel.coefficients[index] ?? 0)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="fit" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Fit a coded model from worksheet data</CardTitle>
              <CardDescription>Choose numeric factor columns and a numeric response column. MapleStats scales factors to coded -1..1 ranges from observed minima and maxima.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                {Array.from({ length: Number(factorCount) }).map((_, index) => (
                  <div key={`factor-column-${index}`} className="space-y-2">
                    <Label>{factorSettings[index]?.name ?? `Factor ${index + 1}`} column</Label>
                    <Select value={factorColumnIds[index] ?? ''} onValueChange={(value) => updateFactorColumn(index, value)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {worksheet.numericColumns.map((column) => (
                          <SelectItem key={column.id} value={column.id}>{column.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
                <div className="space-y-2">
                  <Label>Response column</Label>
                  <Select value={responseColumnId} onValueChange={setResponseColumnId}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {worksheet.numericColumns.map((column) => (
                        <SelectItem key={column.id} value={column.id}>{column.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {!fittedModel ? (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Worksheet fit unavailable</AlertTitle>
              <AlertDescription>Choose distinct numeric factor / response columns with enough variation. MapleStats needs at least four usable rows and a non-singular design matrix.</AlertDescription>
            </Alert>
          ) : (
            <>
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Worksheet-fitted optimum</CardTitle>
                  <CardDescription>Predictions are evaluated on the generated full-factorial corner design.</CardDescription>
                </CardHeader>
                <CardContent className="grid gap-4 md:grid-cols-4">
                  <div className="rounded-lg border p-3">Response: {fittedModel.responseName}</div>
                  <div className="rounded-lg border p-3">Best run: {fittedOptimum?.run}</div>
                  <div className="rounded-lg border p-3">Predicted response: {formatNumber(fittedOptimum?.predicted ?? NaN)}</div>
                  <div className="rounded-lg border p-3">Goal: {goal}</div>
                  {factorSettings.map((factor, index) => (
                    <div key={factor.name} className="rounded-lg border p-3">{factor.name}: {formatNumber(fittedOptimum?.actualLevels[index] ?? NaN)}</div>
                  ))}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Fitted coded coefficients</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Term</TableHead>
                        <TableHead>Coefficient</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {buildTermNames(fittedModel.factorNames).map((term, index) => (
                        <TableRow key={term}>
                          <TableCell>{term}</TableCell>
                          <TableCell>{formatNumber(fittedModel.coefficients[index] ?? 0)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>

      {activeModel && activeOptimum && (
        <div className="flex justify-end">
          <Button type="button" variant="outline">Best predicted corner: Run {activeOptimum.run}</Button>
        </div>
      )}
    </div>
  );
};
